import { Dialog, Transition } from '@headlessui/react'
import React, { Fragment, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import ReactAudioPlayer from 'react-audio-player'
import { ArrowDownTrayIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'
import { getFilePreview } from '../../Requests/NewAnalysisRequests'
import ParticleLoader from "../ParticleLoader";

function FilePreviewModal({ file, handleClose }) {
    const [open, setOpen] = useState(true)
    const [preview, setPreview] = useState(null)
    const [failed, setFailed] = useState(false)
    const { t } = useTranslation()
    const navigate = useNavigate()

    useEffect(() => {
        getFilePreview(file.uuid).then((res) => {
            setPreview(res.data)
        }).catch(() => {
            setFailed(true)
        })
    }, [file.uuid])

    const closeModal = () => {
        handleClose()
        setOpen(false)
    }

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog as="div" className="relative z-10" onClose={() => closeModal()}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 overflow-y-auto">
                    <div className="flex w-full min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative w-full transform overflow-hidden rounded-2xl bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-3xl sm:p-6">
                                <Dialog.Title as="h3" className="text-lg font-semibold text-gray-900">
                                    {file.name}
                                </Dialog.Title>
                                <p className="mt-1 text-sm text-gray-500">
                                    {file.project?.name} · {t(`Files.Type.${file.file_type}`, file.file_type)}
                                </p>

                                <div className="mt-4 min-h-[8rem]">
                                    {!preview && !failed && <div className="flex justify-center"><ParticleLoader size={40} /></div>}
                                    {failed && <p className="text-sm text-gray-500">{t('Files.PreviewUnavailable')}</p>}

                                    {preview && preview.file_type === 'audio' && preview.url &&
                                        <ReactAudioPlayer src={preview.url} controls className="w-full" />
                                    }
                                    {preview && preview.file_type === 'pdf' && preview.url &&
                                        <iframe
                                            title={file.name}
                                            src={preview.url}
                                            className="h-[60vh] w-full rounded-lg border border-gray-200"
                                        />
                                    }
                                    {preview && preview.excerpt &&
                                        <div className="mt-4 max-h-[40vh] overflow-y-auto whitespace-pre-line rounded-lg bg-gray-50 p-4 text-sm text-gray-700">
                                            {preview.excerpt}
                                        </div>
                                    }
                                    {preview && !preview.url && !preview.excerpt &&
                                        <p className="text-sm text-gray-500">{t('Files.PreviewUnavailable')}</p>
                                    }
                                </div>

                                <div className="mt-6 flex flex-wrap justify-end gap-2">
                                    <button
                                        type="button"
                                        className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-900 hover:bg-gray-50"
                                        onClick={() => closeModal()}
                                    >
                                        {t('Files.Close')}
                                    </button>
                                    {preview?.url &&
                                        <a
                                            href={preview.url}
                                            target="_blank"
                                            rel="noreferrer"
                                            download
                                            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-900 hover:bg-gray-50"
                                        >
                                            <ArrowDownTrayIcon className="h-4 w-4" />
                                            {t('Files.Download')}
                                        </a>
                                    }
                                    <button
                                        type="button"
                                        className="inline-flex items-center gap-2 rounded-lg bg-black px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800"
                                        onClick={() => navigate('/project/' + file.project?.uuid + '?tab=0')}
                                    >
                                        <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                                        {t('Files.OpenInProject')}
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    )
}

export default FilePreviewModal
